import React, { useEffect, useState } from "react";
import { useCart } from "../context/CartContext";
import { api } from "../api";
import { ProductCard } from "./Home";
import { toast } from "sonner";
import { Play } from "lucide-react";
import { SEO } from "../components/SEO";
import { usePageContent, resolveMediaUrl, optimizeCloudinaryUrl } from "../components/PageContent";
import { usePageBlocks, PageBlocksRenderer } from "../components/PageBlocks";

const DEFAULT_CATEGORIES = [
  { id: "all", label: "Everything" },
  { id: "decks", label: "Decks" },
  { id: "apparel", label: "Apparel" },
  { id: "accessories", label: "Accessories" },
];

const resolveMedia = (url) => {
  if (!url) return url;
  if (url.startsWith("http")) return optimizeCloudinaryUrl(url);
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const FeaturedVideo = ({ video }) => {
  const [playing, setPlaying] = useState(false);
  if (!video || !video.url) return null;
  const src = resolveMedia(video.url);
  const poster = resolveMedia(video.poster);
  return (
    <section className="border-y-4 border-asphalt bg-asphalt" data-testid="shop-featured-video">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7 relative border-4 border-bone bg-asphalt aspect-video overflow-hidden shadow-hard-bone">
          {playing ? (
            <video
              className="w-full h-full object-cover"
              src={src}
              poster={poster}
              controls
              autoPlay
              playsInline
              data-testid="shop-featured-video-player"
            />
          ) : (
            <button
              onClick={() => setPlaying(true)}
              className="absolute inset-0 w-full h-full group"
              data-testid="shop-featured-video-play"
            >
              {poster ? (
                <img src={poster} alt={video.title || "Featured video"} className="w-full h-full object-cover ink-stamp" />
              ) : (
                <div className="w-full h-full halftone opacity-40" />
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-asphalt/30 group-hover:bg-asphalt/10 transition-colors">
                <div className="border-4 border-bone bg-terra rounded-full p-6 shadow-hard-bone group-hover:scale-110 transition-transform">
                  <Play className="w-10 h-10 text-bone fill-bone" />
                </div>
              </div>
            </button>
          )}
        </div>
        <div className="lg:col-span-5 text-bone">
          <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-3">✦ Now Showing</div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter leading-[0.95] mb-3">
            {video.title || "The Trailer."}
          </h2>
          <p className="font-western text-xl text-bone/85 leading-snug">
            {video.subtitle ||
              "A two-minute taste of what we're doing out here. Watch it loud."}
          </p>
        </div>
      </div>
    </section>
  );
};

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [source, setSource] = useState(null);
  const [cat, setCat] = useState("all");
  const [video, setVideo] = useState(null);
  const [collections, setCollections] = useState([]); // dynamic from Shopify
  const { add } = useCart();
  const c = usePageContent("shop", {
    eyebrow: "The General Store",
    title: "Spend Some Money",
    body: "Decks, hats, prints, and the occasional limited run. Each item handled by at least one of us with reasonably clean hands.",
  });
  const blocks = usePageBlocks("shop");

  // Fetch live Shopify collections so chips reflect the real store layout.
  useEffect(() => {
    api
      .get("/shop/collections")
      .then((r) => setCollections(r.data?.items || []))
      .catch(() => setCollections([]));
  }, []);

  // Build chip list: "Everything" + every Shopify collection. Falls back to hardcoded defaults
  // when the store has no collections yet (or Shopify isn't connected).
  const chips = collections.length
    ? [{ id: "all", label: "Everything" }, ...collections.map((col) => ({ id: col.handle, label: col.title }))]
    : DEFAULT_CATEGORIES;

  useEffect(() => {
    const url =
      cat === "all"
        ? "/shop/products?limit=48"
        : `/shop/products?collection=${encodeURIComponent(cat)}&limit=48`;
    api
      .get(url)
      .then((r) => {
        setProducts(r.data?.items || []);
        setSource(r.data?.source || null);
      })
      .catch(() => setProducts([]));
  }, [cat]);

  useEffect(() => {
    api.get("/settings/shop_featured_video").then((r) => setVideo(r.data?.value)).catch(() => {});
  }, []);

  const filtered = products; // server already filters by tag; no client-side filter needed
  const bg = resolveMediaUrl(c.image);

  return (
    <div className="bg-bone min-h-screen" data-testid="shop-page">
      <SEO
        title={c.title || "Spend Some Money"}
        description={c.body}
        image={bg}
        path="/shop"
      />
      <section
        className="relative bg-asphalt text-bone py-16 overflow-hidden"
        style={
          c.show_media && bg
            ? { backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" }
            : undefined
        }
        data-testid="shop-hero"
      >
        {c.show_media && bg && c.overlay && (
          <div className="absolute inset-0 bg-asphalt/80 mix-blend-multiply pointer-events-none" />
        )}
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10">
          {c.eyebrow && <div className="font-mono text-xs uppercase tracking-[0.35em] text-terra mb-3">✦ {c.eyebrow}</div>}
          <h1 className="font-display text-6xl sm:text-7xl uppercase leading-none tracking-tighter whitespace-pre-line">{c.title}</h1>
          {c.subtitle && <p className="font-western text-2xl text-bone/80 mt-4 max-w-2xl">{c.subtitle}</p>}
          {c.body && <p className="font-western text-2xl text-bone/80 mt-4 max-w-2xl whitespace-pre-line">{c.body}</p>}
        </div>
      </section>
      <div className="tape-divider" />
      <FeaturedVideo video={video} />
      <PageBlocksRenderer blocks={blocks} testid="shop-blocks" />
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10">
        <div className="flex flex-wrap gap-3 mb-10" data-testid="shop-filter-chips">
          {chips.map((chip) => (
            <button
              key={chip.id}
              onClick={() => setCat(chip.id)}
              data-testid={`shop-filter-${chip.id}`}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.25em] border-2 border-asphalt transition-colors ${
                cat === chip.id ? "bg-asphalt text-bone" : "bg-bone hover:bg-asphalt hover:text-bone"
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="shop-grid">
          {filtered.map((p, i) => (
            <ProductCard
              product={p}
              key={p.id}
              index={i}
              onAdd={(prod) => {
                add(prod);
                toast.success(`${prod.name} added to the bag.`);
              }}
            />
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="border-2 border-asphalt bg-card-light shadow-hard p-10 text-center" data-testid="shop-empty-state">
            <div className="font-western text-2xl mb-1">
              {cat === "all" ? "Nothing for sale yet." : `Nothing in ${chips.find((x) => x.id === cat)?.label || cat} yet.`}
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-ink">
              {cat === "all" ? "Check back soon, partner." : "Try a different category, partner."}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
